<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50 tw-flex items-center justify-center p-4"
      @click.self="$emit('update:modelValue', false)"
    >
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="$emit('update:modelValue', false)"/>

      <!-- Modal -->
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] tw-flex flex-col overflow-hidden">

        <!-- Header buttons: options + close -->
        <div class="absolute top-3 right-3 z-10 tw-flex items-center gap-1">
          <DropdownActionsButton :actions="menuActions">
            <template #button>
              <button class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 border-0 cursor-pointer transition-colors text-gray-500">
                <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 8a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>
                </svg>
              </button>
            </template>
          </DropdownActionsButton>
          <button
            class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 border-0 cursor-pointer transition-colors text-gray-500"
            @click="$emit('update:modelValue', false)"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Scrollable content -->
        <div class="overflow-y-auto flex-1 rounded-t-2xl">
          <!-- Image -->
          <div v-if="image" class="h-52 overflow-hidden rounded-t-2xl bg-gray-100 shrink-0 relative">
            <div v-if="!imageLoaded" class="absolute inset-0 bg-gray-200 animate-pulse"/>
            <img
              v-if="deferredImage"
              :src="deferredImage"
              :alt="announcement.title"
              class="w-full h-full object-cover transition-opacity duration-300"
              :class="imageLoaded ? 'opacity-100' : 'opacity-0'"
              @load="imageLoaded = true"
            />
          </div>

          <div class="p-5">
            <!-- Author row -->
            <div class="tw-flex items-center gap-2 mb-3">
              <AvatarNew
                showDetails
                showClaim
                v-if="announcement.user && announcement.user.photo_url"
                :user="announcement.user"
              />
            </div>

            <!-- Title -->
            <h2 class="text-lg font-bold text-gray-900 leading-snug mb-2">{{ announcement.title }}</h2>

            <!-- Body -->
            <div class="text-sm text-gray-700 leading-relaxed prose max-w-none" v-html="announcement.body || announcement.shortDescription"/>

            <!-- Engagement bar -->
            <div class="mt-4 pt-3 border-t border-gray-100 tw-flex items-center gap-5">
              <button
                class="tw-flex items-center gap-1.5 text-sm border-0 bg-transparent p-0 cursor-pointer transition-colors"
                :class="liked ? 'text-red-500' : 'text-gray-500 hover:text-red-400'"
                @click="toggleLike"
              >
                <svg class="h-5 w-5 transition-all" :fill="liked ? 'currentColor' : 'none'" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                </svg>
                <span>{{ likeCount }} {{ $t('personal_home_likes') }}</span>
              </button>
              <span class="tw-flex items-center gap-1.5 text-sm text-gray-500">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
                {{ comments?.comments?.length }} {{ $t('personal_home_comments') }}
              </span>
            </div>

            <!-- Comments -->
            <div class="mt-4">
              <!-- Loading -->
              <div v-if="loadingComments" class="tw-flex justify-center py-4">
                <svg class="h-5 w-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              </div>

              <div v-else class="space-y-3">
                <div
                  v-for="comment in comments.comments"
                  :key="comment.id"
                  class="tw-flex gap-3 group"
                >
                  <div class="h-8 w-8 shrink-0 rounded-full bg-gray-200 overflow-hidden tw-flex items-center justify-center">
                    <AvatarNew :user="comment.user" v-if="comment.user"/>
                    <span v-else class="text-xs font-semibold text-gray-500">{{ (comment.user_name || '?')[0].toUpperCase() }}</span>
                  </div>
                  <div class="flex-1 bg-gray-50 rounded-xl px-3 py-2">
                    <div class="tw-flex items-baseline justify-between gap-2">
                      <div class="tw-flex items-baseline gap-2">
                        <span class="text-xs font-semibold text-gray-800">{{ comment.user_name }}</span>
                        <span class="text-[10px] text-gray-400">{{ formatCommentDate(comment.created_at) }}</span>
                      </div>
                      <DropdownActionsButton
                        v-if="canDeleteComment(comment)"
                        :actions="[{ label: $t('delete'), icon: 'trash', variant: 'danger', onClick: () => deleteComment(comment) }]"
                        class="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <template #button>
                          <button class="inline-flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:text-gray-600 border-0 bg-transparent cursor-pointer">
                            <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 8a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>
                            </svg>
                          </button>
                        </template>
                      </DropdownActionsButton>
                    </div>
                    <p class="text-xs text-gray-700 mt-0.5 mb-0" v-html="comment.comment"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky comment input -->
        <div class="shrink-0 border-t border-gray-100 px-5 py-3 bg-white tw-flex gap-3 rounded-b-2xl">
          <div class="flex-1">
            <textarea
              v-model="newComment"
              :placeholder="$t('announcement_new_comment_cta')"
              rows="1"
              class="w-full resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-primary bg-gray-50"
              @keydown.enter.exact.prevent="submitComment"
            />
          </div>
          <button
            :disabled="!newComment.trim() || submittingComment"
            class="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-primary border-0 text-white cursor-pointer disabled:opacity-40 disabled:cursor-default transition-opacity shrink-0"
            @click="submitComment"
          >
            <Icon name="send" size="14"/>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
  import { getImageForAnnouncement } from '@/util/helpers'
  import { createComment, deleteComment, getComments } from '@/api/notifications'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import { toggleLike } from '@/Domain/Announcements/Api/notifications'
  import Icon from '@/components/Icon/Icon.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import DropdownActionsButton from '@/Domain/Shared/Components/DropdownActionsButton.vue'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'

  export default {
    name: 'PersonalHomeAnnouncementModal',
    components: { Icon, AvatarNew, DropdownActionsButton },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      announcement: {
        type: Object,
        required: true,
      },
    },
    emits: ['update:modelValue'],
    data () {
      return {
        liked: !!this.announcement.user_like,
        likeCount: this.announcement.likers ? this.announcement.likers.length : 0,
        comments: [],
        loadingComments: false,
        newComment: '',
        submittingComment: false,
        imageLoaded: false,
        deferredImage: null,
      }
    },
    computed: {
      image () {
        return getImageForAnnouncement(this.announcement)
      },
      shareUrl () {
        return `${window.location.origin}/announcements/${this.announcement.id}`
      },
      canEditOrDelete () {
        return this.$store.getters.isOwner || this.$store.getters.userId === this.announcement?.user?.id
      },
      menuActions () {
        const actions = [
          { label: 'LinkedIn', icon: 'linkedin-logo', onClick: () => this.shareToNetwork('linkedin') },
          { label: 'X', icon: 'twitter-logo', onClick: () => this.shareToNetwork('twitter') },
          { label: 'Facebook', icon: 'facebook-logo', onClick: () => this.shareToNetwork('facebook') },
        ]
        if (this.canEditOrDelete) {
          actions.push(
            { label: this.$t('edit'), icon: 'edit', onClick: () => this.editAnnouncement() },
            { label: this.$t('delete'), icon: 'trash', variant: 'danger', onClick: () => this.confirmDeleteAnnouncement() },
          )
        }
        return actions
      },
    },
    mounted () {
      if (this.modelValue) {
        this.deferredImage = this.image
        this.loadComments()
      }
    },
    watch: {
      modelValue (val) {
        if (val) {
          this.imageLoaded = false
          this.deferredImage = null
          this.$nextTick(() => {
            this.deferredImage = this.image
          })
          this.loadComments()
        }
      },
      announcement (val) {
        this.liked = !!val.user_like
        this.likeCount = val.likers ? val.likers.length : 0
      },
    },
    methods: {
      formatCommentDate (dateStr) {
        if (!dateStr) return ''
        const date = new Date(dateStr)
        const now = new Date()
        const diff = Math.floor((now - date) / 1000)
        if (diff < 60) return `${diff}s`
        if (diff < 3600) return `${Math.floor(diff / 60)}m`
        if (diff < 86400) return `${Math.floor(diff / 3600)}h`
        if (diff < 604800) return `${Math.floor(diff / 86400)}d`
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      },
      async loadComments () {
        this.loadingComments = true
        try {
          const result = await getComments(this.announcement.id)
          this.comments = result.data || result || []
        } catch {
          this.comments = []
        } finally {
          this.loadingComments = false
        }
      },
      async toggleLike () {
        const newLiked = !this.liked
        this.liked = newLiked
        this.likeCount += newLiked ? 1 : -1
        try {
          //await likeNotification(this.announcement.id, newLiked ? 1 : 0)
          await toggleLike(this.announcement.id)
        } catch {
          this.liked = !newLiked
          this.likeCount += newLiked ? -1 : 1
        }
      },
      shareToNetwork (network) {
        const url = encodeURIComponent(this.shareUrl)
        const title = encodeURIComponent(this.announcement.title)
        const shareUrls = {
          linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
          twitter: `https://twitter.com/intent/tweet?text=${title}&url=${url}&via=datascouts`,
          facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        }
        window.open(shareUrls[network], '_blank', 'width=600,height=400')
      },
      editAnnouncement () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { announcement: this.announcement })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.MANAGE_ANNOUNCEMENT)
      },
      confirmDeleteAnnouncement () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          modalContextType: 'announcement',
          name: this.announcement.title,
          announcement: this.announcement,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      canDeleteComment (comment) {
        return this.$store.getters.isOwner || this.$store.getters.userId === comment.user_id
      },
      async deleteComment (comment) {
        try {
          await deleteComment(this.announcement.id, comment.id)
          this.comments.comments = this.comments.comments.filter(c => c.id !== comment.id)
        } catch {
          // silently ignore
        }
      },
      async submitComment () {
        const body = this.newComment.trim()
        if (!body || this.submittingComment) return
        this.submittingComment = true
        try {
          const result = await createComment(this.announcement.id, body)
          this.newComment = ''

          trackMatomoEvent(MATOMO_EVENT_CATEGORIES.ANNOUNCEMENT_COMMENT, MATOMO_EVENT_ACTIONS.COMMENT, this.announcement.id)

          await this.loadComments()
        } catch {
          // silently ignore
        } finally {
          this.submittingComment = false
        }
      },
    },
  }
</script>
